import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import SectionTitle from "@/components/feature/SectionTitle";

export default function Detail() {
  const { id = "" } = useParams();
  const movie = movies.find((m) => m.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  usePageMeta({
    title: movie ? `${movie.title} - 电影天堂详情` : "影片未找到 - 电影天堂",
    description: movie
      ? `${movie.title}（${movie.year}）由${movie.director}执导，${movie.cast.slice(0, 2).join("、")}主演。${movie.synopsis.slice(0, 60)}…`
      : "未找到该影片详情，请返回电影天堂首页浏览其他内容。",
    keywords: movie
      ? `${movie.title},${movie.title}详情,${movie.types.join(",")},电影天堂`
      : "电影天堂,电影详情",
  });

  const related = useMemo(() => {
    if (!movie) return [];
    const sameType = movies.filter(
      (m) => m.id !== movie.id && m.types.some((t) => movie.types.includes(t))
    );
    const sameDirector = movies.filter(
      (m) => m.id !== movie.id && m.director === movie.director
    );
    const merged = [...sameDirector, ...sameType].filter(
      (m, i, arr) => arr.findIndex((x) => x.id === m.id) === i
    );
    return merged.slice(0, 5);
  }, [movie]);

  if (!movie) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="没有找到这部影片"
          description="影片可能已下架或链接有误，你可以返回首页或浏览其他分类。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const fav = isFavorite(movie.id);
  const movieCategories = categories.filter((c) => movie.types.includes(c.name));

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2200);
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制到剪贴板");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      {/* 面包屑 */}
      <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-sm text-foreground-600">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span>电影详情</span>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">{movie.title}</span>
      </nav>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr]">
        {/* 海报 */}
        <div className="mx-auto w-full max-w-[300px] lg:mx-0">
          <div className="aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
            <img
              src={movie.poster}
              alt={`${movie.title}（${movie.year}）电影海报`}
              title={`${movie.title} 高清海报`}
              className="h-full w-full object-top object-cover"
            />
          </div>
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={() => {
                toggleFavorite(movie.id);
                showToast(fav ? "已取消收藏" : "已加入收藏");
              }}
              className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                fav
                  ? "bg-accent-500 text-primary-950"
                  : "border border-background-300 bg-background-50 text-foreground-800 hover:border-primary-400 hover:text-primary-600"
              }`}
            >
              <i className={fav ? "ri-heart-fill" : "ri-heart-line"}></i>
              {fav ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="flex flex-1 items-center justify-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              <i className="ri-share-line"></i>
              分享
            </button>
          </div>
          <p className="mt-2 text-center text-xs text-foreground-500">
            收藏为本机演示功能，数据仅保存在当前浏览器
          </p>
        </div>

        {/* 信息 */}
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-heading text-3xl font-black text-foreground-950">{movie.title}</h1>
            <span className="flex items-center gap-1 rounded-md bg-accent-100 px-2.5 py-1 text-sm font-bold text-accent-800">
              <i className="ri-star-fill"></i>
              {movie.rating.toFixed(1)}
            </span>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {movie.types.map((t) => (
              <span
                key={t}
                className="rounded-md bg-secondary-100 px-2.5 py-1 text-sm text-secondary-900"
              >
                {t}
              </span>
            ))}
          </div>

          <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 rounded-lg border border-background-200 bg-background-50 p-5 sm:grid-cols-2">
            <div className="flex gap-2 text-sm">
              <dt className="w-16 shrink-0 text-foreground-500">上映时间</dt>
              <dd className="text-foreground-900">{movie.releaseDate}</dd>
            </div>
            <div className="flex gap-2 text-sm">
              <dt className="w-16 shrink-0 text-foreground-500">影片时长</dt>
              <dd className="text-foreground-900">{movie.duration} 分钟</dd>
            </div>
            <div className="flex gap-2 text-sm">
              <dt className="w-16 shrink-0 text-foreground-500">导演</dt>
              <dd className="text-foreground-900">{movie.director}</dd>
            </div>
            <div className="flex gap-2 text-sm">
              <dt className="w-16 shrink-0 text-foreground-500">地区</dt>
              <dd className="text-foreground-900">{movie.region}</dd>
            </div>
            <div className="flex gap-2 text-sm sm:col-span-2">
              <dt className="w-16 shrink-0 text-foreground-500">主演</dt>
              <dd className="text-foreground-900">{movie.cast.join("、")}</dd>
            </div>
          </dl>

          <div className="mt-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
            <p className="mt-3 text-sm leading-7 text-foreground-700 md:text-base">
              {movie.synopsis}
            </p>
          </div>

          {movieCategories.length > 0 && (
            <div className="mt-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">所属分类</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {movieCategories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-background-300 bg-background-50 px-3.5 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                  >
                    <i className={`${c.icon}`}></i>
                    {c.name}电影
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 相关推荐 */}
      {related.length > 0 && (
        <section className="mt-14">
          <SectionTitle
            title="相关推荐"
            subtitle="根据类型与导演为你匹配的影片"
            icon="ri-magic-line"
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {related.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 rounded-md bg-foreground-950 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}